import React, { useEffect, useState } from 'react'
import {
  IonCol,
  IonGrid,
  IonItem,
  IonItemDivider,
  IonLabel,
  IonRow,
  IonTextarea,
  useIonViewDidEnter,
  useIonViewDidLeave,
} from '@ionic/react'
import { useDispatch, useSelector } from 'react-redux'
import { IProcedureFooter } from './Procedure.component'
import MultiSelect from '../multiSelect/MultiSelect.component'
import SingleSelect from '../singleSelect/SingleSelect.component'
import { getProcedureStateSelector } from './selectors'
import { makeActionCreator } from '../../utilities'
import useDeviceMetrics from '../../utilities/useDeviceMetrics'
import { useTranslation } from 'react-i18next'
import { IOption, IPNNVOption } from '../../common/common.types'
import Validation from '../validation/Validation.component'
import { getStyleForPHI, IPHIComponent } from '../phi/phi.component'
import textAreaStyles from '../textArea/TextArea.component.module.css'
import SInput from '../stringInput/StringInput.component'
import { getCriticalCareSelector } from '../../features/procedures/criticalCare/selectors'
import { setSize } from '../../features/procedures/criticalCare/store/criticalCare.actions'

export interface IProcedureFooterComponentProps extends IProcedureFooter, IPHIComponent {
  procedureType: string
}

const ProcedureFooterComponent: React.FC<IProcedureFooterComponentProps> = (props: IProcedureFooterComponentProps) => {
  const { procedureType, procedureComplications, response, responsePNNV, authorization, comments, isPHI } = props
  const procedureState = useSelector(getProcedureStateSelector)

  const criticalCare = useSelector(getCriticalCareSelector)
  const { size } = criticalCare

  const { authorizationOptions, complicationsOptions, responseOptions } = procedureState
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [flag, setFlag] = useState(true)
  const [isFocused, setIsFocused] = useState(false)
  const { isTablet: isTabletDevice } = useDeviceMetrics()

  const onFocus = () => {
    setIsFocused(true)
  }

  const onBlur = () => {
    setIsFocused(false)
  }

  const setSizeOnChange = (e: CustomEvent) => {
    dispatch(setSize(e.detail.value))
  }

  useEffect(() => {
    if (flag) {
      comments && dispatch({ type: `${procedureType}/SET_COMMENTS`, comments: comments })
    }
  }, [flag])

  useIonViewDidEnter(() => {
    setFlag(true)
  })

  useIonViewDidLeave(() => {
    setFlag(false)
  })

  return (
    <>
      <IonGrid className='ion-no-padding'>
        {procedureComplications && (
          <>
            <IonRow>
              <IonCol className='picklist'>
                <MultiSelect
                  dataTestID='ProcedureComplications'
                  listHeader={t('procedure.footer.procedureComplications')}
                  options={complicationsOptions}
                  savedValues={procedureComplications ? procedureComplications : []}
                  action={makeActionCreator(`${procedureType}/SET_PROCEDURE_COMPLICATIONS`, 'procedureComplications')}
                  label={t('procedure.footer.procedureComplications')}
                />
              </IonCol>
            </IonRow>
            <hr className='field-divider' />
          </>
        )}

        {response !== undefined && (
          <>
            <IonRow>
              <IonCol className='picklist'>
                <SingleSelect
                  dataTestID='Response'
                  selectedOption={response}
                  listHeader={t('procedure.footer.response')}
                  label={t('procedure.footer.response')}
                  options={responseOptions}
                  action={(option: IOption) => dispatch({ type: `${procedureType}/SET_RESPONSE`, response: option })}
                  selectedPNNVOption={responsePNNV}
                  pnnvAction={(option: IPNNVOption) =>
                    dispatch({ type: `${procedureType}/SET_RESPONSE_PNNV`, responsePNNV: option })
                  }
                  showPNNVs={procedureType !== 'labs'} // TODO: To remove when removing Response field from Labs Procedure
                />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />
          </>
        )}

        {procedureType === 'criticalCare' && (
          <>
            <IonRow>
              <IonCol className='picklist'>
                <SInput
                  label={t('criticalCare.size')}
                  dataTestID='Size'
                  inputValue={size}
                  onChangeAction={setSizeOnChange}
                />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />
          </>
        )}

        {authorization !== undefined && (
          <>
            <IonRow>
              <IonCol className='picklist'>
                <SingleSelect
                  dataTestID='Authorization'
                  selectedOption={authorization}
                  listHeader={t('procedure.footer.authorization')}
                  label={t('procedure.footer.authorization')}
                  options={authorizationOptions}
                  action={(option: IOption) =>
                    dispatch({ type: `${procedureType}/SET_AUTHORIZATION`, authorization: option })
                  }
                />
              </IonCol>
            </IonRow>
            <hr className='field-divider' />
          </>
        )}

        <IonRow>
          <IonCol>
            <IonItem lines='none'>
              <IonLabel className={textAreaStyles['text-area-label']} position='stacked'>
                {t('procedure.footer.comments')}
              </IonLabel>
              <div data-private={isPHI} style={getStyleForPHI(isPHI)}>
                <IonTextarea
                  data-testid='Comments'
                  rows={3}
                  cols={20}
                  value={comments}
                  color='primary-alt'
                  onIonChange={(e) => dispatch({ type: `${procedureType}/SET_COMMENTS`, comments: e.detail.value })}
                  maxlength={1000}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  autocapitalize='sentences'
                />
              </div>
              <div className='footer'>
                <span>{comments.length >= 1000 && <Validation id={`${procedureType}/SET_COMMENTS`} />}</span>
                <h1 className={comments.length < 1000 ? 'text-color-blue-tint' : 'text-color-red'}>
                  {comments.length}/1000
                </h1>
              </div>
            </IonItem>
          </IonCol>
        </IonRow>
      </IonGrid>

      {isFocused && (
        <IonItemDivider color='light' className={isTabletDevice ? 'bottom-divider-tablet' : 'bottom-divider-mobile'} />
      )}
    </>
  )
}

export default ProcedureFooterComponent
